import React, { useState } from 'react';
import { paidChannels } from '../../mock';
import { Check, ThumbsUp, MessageCircle, Share, Heart, Bookmark, Repeat2, BadgeCheck, MoreHorizontal, Send } from 'lucide-react';

/* ============================================================
   Meta / Facebook Ad — accurate replica of newsfeed sponsored post
============================================================ */
function MetaAd({ channel }) {
  const { sampleAd, adPreview } = channel;
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-2xl max-w-xs w-full text-[#1c1e21]">
      {/* header */}
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4ECDC4] to-[#0F1C40] flex items-center justify-center text-white text-[11px] font-bold">
          AC
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <div className="text-[13px] font-semibold text-[#050505]">{sampleAd.brand}</div>
            <BadgeCheck className="w-3.5 h-3.5 text-[#1877F2]" strokeWidth={2.5} />
          </div>
          <div className="flex items-center gap-1 text-[11px] text-[#65676b]">
            <span>Sponsored</span>
            <span>·</span>
            <span>🌐</span>
          </div>
        </div>
        <MoreHorizontal className="w-4 h-4 text-[#65676b]" />
      </div>
      {/* copy */}
      <div className="px-3 pb-2 text-[13px] text-[#050505] leading-snug">
        {sampleAd.headline} <span className="text-[#1877F2]">#DevOps #SaaS</span>
      </div>
      {/* media */}
      <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
        <img src={adPreview} alt="" className="w-full h-full object-cover" />
      </div>
      {/* link module */}
      <div className="px-3 py-2 flex items-center justify-between bg-[#f0f2f5] border-b border-[#dadde1]">
        <div className="min-w-0">
          <div className="text-[10px] text-[#65676b] uppercase tracking-wider">acmecloud.com</div>
          <div className="text-[13px] font-semibold text-[#050505] truncate">{sampleAd.cta}</div>
          <div className="text-[10px] text-[#65676b]">Cut deploy time — try free for 14 days</div>
        </div>
        <button className="shrink-0 ml-2 bg-[#e4e6eb] hover:bg-[#d8dadf] text-[#050505] text-[11px] font-semibold px-3 py-1.5 rounded-md">
          Sign Up
        </button>
      </div>
      {/* reactions */}
      <div className="px-3 py-2 flex items-center justify-between text-[11px] text-[#65676b] border-b border-[#dadde1]">
        <div className="flex items-center gap-1">
          <div className="flex -space-x-1">
            <div className="w-4 h-4 rounded-full bg-[#1877F2] flex items-center justify-center text-[8px] text-white">
              👍
            </div>
            <div className="w-4 h-4 rounded-full bg-[#F33E58] flex items-center justify-center text-[8px]">
              ❤️
            </div>
          </div>
          <span>2.4K</span>
        </div>
        <div className="flex items-center gap-2">
          <span>184 comments</span>
          <span>·</span>
          <span>62 shares</span>
        </div>
      </div>
      {/* actions */}
      <div className="flex items-center justify-around px-2 py-1 text-[#65676b] text-[11px]">
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-[#f0f2f5]">
          <ThumbsUp className="w-4 h-4" strokeWidth={1.5} /> Like
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-[#f0f2f5]">
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Comment
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-[#f0f2f5]">
          <Share className="w-4 h-4" strokeWidth={1.5} /> Share
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Google Search Ad — accurate SERP style
============================================================ */
function GoogleAd({ channel }) {
  const { sampleAd, adPreview } = channel;
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-2xl max-w-sm w-full text-[#202124]">
      {/* browser chrome */}
      <div className="px-3 py-2.5 border-b border-neutral-200 flex items-center gap-2">
        <div className="flex gap-1">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 mx-3 h-6 bg-[#f1f3f4] rounded-full text-[10px] font-medium text-[#5f6368] flex items-center px-3">
          <span className="mr-1">🔒</span>
          <span>google.com/search?q=bi+platform</span>
        </div>
      </div>
      {/* google logo bar */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-3">
        <div className="text-lg font-medium">
          <span className="text-[#4285F4]">G</span>
          <span className="text-[#EA4335]">o</span>
          <span className="text-[#FBBC05]">o</span>
          <span className="text-[#4285F4]">g</span>
          <span className="text-[#34A853]">l</span>
          <span className="text-[#EA4335]">e</span>
        </div>
        <div className="flex-1 h-8 rounded-full border border-neutral-200 px-3 flex items-center text-[11px] text-[#5f6368]">
          bi platform for data teams
        </div>
      </div>
      <div className="px-4 text-[10px] text-[#5f6368] border-b border-neutral-100 pb-2">
        About 41,200,000 results (0.42 seconds)
      </div>

      {/* Sponsored result */}
      <div className="px-4 py-3 space-y-1">
        <div className="flex items-center gap-1.5 text-[11px] text-[#202124]">
          <span className="font-bold">Sponsored</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-[#4ECDC4] flex items-center justify-center text-[9px] text-white font-bold">
            N
          </div>
          <div className="text-[11px] text-[#202124]">
            <div className="font-medium leading-tight">Nova Analytics</div>
            <div className="text-[10px] text-[#5f6368]">https://novaanalytics.com</div>
          </div>
        </div>
        <div className="text-[16px] text-[#1a0dab] hover:underline cursor-pointer leading-tight font-normal">
          {sampleAd.headline}
        </div>
        <p className="text-[12px] text-[#4d5156] leading-relaxed">
          Ranked #1 by G2. Trusted by 5,000+ data teams. Real-time dashboards, anomaly detection &amp; AI insights — all in one platform.
        </p>
        <div className="flex items-center gap-3 text-[11px] text-[#1a0dab] pt-1">
          <span className="hover:underline cursor-pointer">Product</span>
          <span className="text-neutral-300">·</span>
          <span className="hover:underline cursor-pointer">Pricing</span>
          <span className="text-neutral-300">·</span>
          <span className="hover:underline cursor-pointer">{sampleAd.cta}</span>
        </div>
      </div>

      {/* Display banner preview (below the fold on GDN) */}
      <div className="px-4 pb-4">
        <div className="text-[9px] font-mono uppercase text-[#5f6368] mb-1.5 tracking-widest">GDN · Display Ad</div>
        <div className="relative rounded-md overflow-hidden border border-neutral-200">
          <img src={adPreview} alt="" className="w-full h-24 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1C40]/80 via-[#0F1C40]/50 to-transparent" />
          <div className="absolute inset-0 p-3 flex flex-col justify-between">
            <div className="text-[9px] font-mono uppercase text-white/70 tracking-widest">Nova Analytics</div>
            <div>
              <div className="text-[11px] text-white font-semibold leading-tight max-w-[65%]">
                Ship dashboards 3× faster with AI.
              </div>
              <button className="mt-1 text-[9px] font-semibold text-[#0F1C40] bg-[#4ECDC4] px-2 py-1 rounded">
                Book Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   LinkedIn Sponsored Post — accurate feed style
============================================================ */
function LinkedInAd({ channel }) {
  const { sampleAd, adPreview } = channel;
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-2xl max-w-sm w-full text-[#000000E6]">
      {/* header */}
      <div className="px-4 pt-3 pb-2 flex items-start gap-2.5">
        <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#0077B5] to-[#0F1C40] shrink-0 flex items-center justify-center text-white font-bold text-[13px]">
          SS
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <div className="text-[13px] font-semibold leading-tight">{sampleAd.brand}</div>
            <BadgeCheck className="w-3.5 h-3.5 text-[#0077B5]" strokeWidth={2.5} />
          </div>
          <div className="text-[11px] text-[#00000099]">12,483 followers</div>
          <div className="text-[10px] text-[#00000099] flex items-center gap-1 mt-0.5">
            <span>Promoted</span>
            <span>·</span>
            <span>🌐</span>
          </div>
        </div>
        <button className="shrink-0 text-[#0077B5] text-[13px] font-semibold hover:bg-[#0077B510] px-2 py-1 rounded">
          + Follow
        </button>
      </div>
      {/* body */}
      <div className="px-4 pb-2.5 text-[13px] leading-snug">
        {sampleAd.headline} — the definitive guide for security leaders. Download the 2026 ROI report ↓
        <div className="mt-1 text-[#0077B5] text-[12px]">#CISO #CyberSecurity #ROI</div>
      </div>
      {/* media */}
      <div className="relative aspect-[16/10] bg-neutral-100 overflow-hidden">
        <img src={adPreview} alt="" className="w-full h-full object-cover" />
        {/* overlay label mimicking LinkedIn document/PDF card */}
        <div className="absolute bottom-0 left-0 right-0 bg-white px-3 py-2 border-t border-neutral-100">
          <div className="text-[10px] text-[#00000099] uppercase tracking-wide">sentinel.com</div>
          <div className="text-[13px] font-semibold text-[#000000E6] leading-snug truncate">
            The 2026 ROI report for CISOs
          </div>
        </div>
      </div>
      {/* CTA button */}
      <div className="px-3 py-2">
        <button className="w-full bg-[#0A66C2] hover:bg-[#004182] text-white text-[13px] font-semibold py-1.5 rounded-full">
          {sampleAd.cta}
        </button>
      </div>
      {/* reactions strip */}
      <div className="px-3 pt-1 pb-1 flex items-center justify-between text-[11px] text-[#00000099] border-b border-neutral-100">
        <div className="flex items-center gap-1">
          <div className="flex -space-x-1">
            <div className="w-4 h-4 rounded-full bg-[#0A66C2] flex items-center justify-center text-[7px] text-white">
              👍
            </div>
            <div className="w-4 h-4 rounded-full bg-[#F5987E] flex items-center justify-center text-[7px]">
              💡
            </div>
            <div className="w-4 h-4 rounded-full bg-[#DF704D] flex items-center justify-center text-[7px]">
              🎉
            </div>
          </div>
          <span>Sarah Chen and 1,204 others</span>
        </div>
        <span>62 comments</span>
      </div>
      {/* actions */}
      <div className="flex items-center justify-around px-2 py-1.5 text-[#00000099] text-[11px]">
        <button className="flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-100">
          <ThumbsUp className="w-4 h-4" strokeWidth={1.5} /> Like
        </button>
        <button className="flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-100">
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Comment
        </button>
        <button className="flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-100">
          <Repeat2 className="w-4 h-4" strokeWidth={1.5} /> Repost
        </button>
        <button className="flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-100">
          <Send className="w-4 h-4" strokeWidth={1.5} /> Send
        </button>
      </div>
    </div>
  );
}

function AdMockup({ channel }) {
  if (channel.id === 'meta') return <MetaAd channel={channel} />;
  if (channel.id === 'google') return <GoogleAd channel={channel} />;
  return <LinkedInAd channel={channel} />;
}

export default function PaidMedia() {
  const [active, setActive] = useState(paidChannels[0].id);
  const channel = paidChannels.find((c) => c.id === active);

  return (
    <section className="py-20 lg:py-32 bg-[#0A1530] text-[#F1EDE4] border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Paid Media</span>
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="text-4xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.02] font-medium">
              Precision targeting across the world&apos;s most powerful{' '}
              <span className="font-serif italic font-normal text-[#4ECDC4]">paid channels.</span>
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10">
          {paidChannels.map((c) => (
            <button
              key={c.id}
              data-testid={`paid-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors duration-200 ${
                active === c.id
                  ? 'border-[#FFB020] text-[#F1EDE4]'
                  : 'border-transparent text-white/50 hover:text-white/80'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <div className="text-[11px] font-mono uppercase tracking-widest text-white/50 mb-3">{channel.sub}</div>
            <div className="text-6xl lg:text-8xl font-serif italic tracking-tighter mb-2 tabular text-[#F1EDE4]">
              {channel.stat}
            </div>
            <div className="text-white/50 text-sm mb-8">{channel.statLabel}</div>
            <div className="border-t border-white/10 pt-6">
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/50 mb-2">Best For</div>
              <div className="text-lg font-medium mb-6">{channel.bestFor}</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/50 mb-2">Ad Formats</div>
              <ul className="space-y-2">
                {channel.formats.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                    <Check className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Ad preview mockup */}
          <div className="lg:col-span-4 flex justify-center">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#4ECDC4]/30 via-transparent to-[#FFB020]/25 blur-3xl -z-10" />
              <AdMockup channel={channel} />
              <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-white/40 text-center">
                Sample creative · preview
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-3">Targeting Options</div>
              <p className="text-[13px] text-white/70 leading-relaxed">{channel.targeting}</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mt-4">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#FFB020] mb-3">Sample audience</div>
              <p className="text-[13px] text-white/70 leading-relaxed">{channel.sampleAd.audience}</p>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs">
                <span className="text-white/50">Est. reach</span>
                <span className="font-mono font-semibold text-[#FFB020]">142K – 165K</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
